import React from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import { type RouteComponentProps, Redirect, Route, Switch } from 'react-router';

import LazyComponent from '@fd/ui/LazyComponent';

import { lazyWithRetry } from '../../helpers/utilities';
import ErrorBoundary from '../../layouts/Portal/ErrorBoundary';
import { PortalFeatureFlags } from '../../services/featureFlags/portalFeatureFlags';
import { useFeatureFlagReady } from '../../services/featureFlags/useFeatureFlagReady';
import { useFeatureFlagValue } from '../../services/featureFlags/useFeatureFlagValue';
import { flagService } from '../../services/flagService/flagService';
import { AppLoadingCircularProgress } from '../AppLoadingCircularProgress';
import { getMenuListingPath } from '../MenusV3/menuListingPath';

const MenuActions = lazyWithRetry(() => import('./MenuActions'));

type Props = RouteComponentProps<{ appId: string }>;

const MenuActionsRoutes: React.FC<Props> = (props: Props) => {
  const {
    match: { path, url, params },
  } = props;
  const showMenuSectionNav = useFeatureFlagValue(
    PortalFeatureFlags.menuV3_showMenuSectionNav,
    false
  );
  const isMenuSectionNavReady = useFeatureFlagReady(PortalFeatureFlags.menuV3_showMenuSectionNav);
  const isOrgListingV3On = useFeatureFlagValue(PortalFeatureFlags.showMenuListingV3Org, false);
  const isOrgListingV3Ready = useFeatureFlagReady(PortalFeatureFlags.showMenuListingV3Org);
  const isLegacyListingV3On = useAppSelector((state) =>
    flagService.isFlagOn(state, 'showMenuListingV3')
  );
  const isLegacyListingV3Ready = useAppSelector((state) =>
    flagService.isFlagReady(state, 'showMenuListingV3')
  );

  if (!isMenuSectionNavReady) {
    return <AppLoadingCircularProgress />;
  }

  if (!showMenuSectionNav) {
    const isListingV3Enabled = isLegacyListingV3On || isOrgListingV3On;
    const bothListingFlagsResolved = isLegacyListingV3Ready && isOrgListingV3Ready;
    // A resolved-off listing flag must not choose the legacy editor while the other flag is still pending.
    if (!isListingV3Enabled && !bothListingFlagsResolved) {
      return <AppLoadingCircularProgress />;
    }

    const listingPath = getMenuListingPath(
      params.appId,
      isLegacyListingV3On,
      isLegacyListingV3Ready,
      isOrgListingV3On,
      isOrgListingV3Ready
    );
    if (!listingPath) {
      return <AppLoadingCircularProgress />;
    }
    return <Redirect to={listingPath} />;
  }

  return (
    <ErrorBoundary identifier="menu-actions">
      <Switch>
        <Route exact path={path} render={() => <Redirect to={`${url}/menu-actions`} />} />
        <Route
          path={path}
          render={() => (
            <LazyComponent>
              <MenuActions />
            </LazyComponent>
          )}
        />
      </Switch>
    </ErrorBoundary>
  );
};

export default MenuActionsRoutes;
